// MainTable.jsx (DROP-IN REPLACEMENT)
import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import Table from "./Table";
import "./App.css";
import { columns, getData } from "./data";
import useDidMountEffect from "./useDidMountEffect";
import Pagination from "./Pagination";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment-timezone";
import Dashboard from "./Dashboard";
import { DEFAULT_FILTERS } from "./FilterPanel";
import { isSSEConnected } from "./SSE";

/* Helper: check if any panel filters are active (excludes date range) */
const isPanelFiltersActive = (f) => {
  if (!f) return false;
  return (
    (f.severity && f.severity !== "any") ||
    (f.dayNight && f.dayNight !== "any") ||
    (Array.isArray(f.tagKeys) && f.tagKeys.length > 0) ||
    (Array.isArray(f.makes) && f.makes.length > 0) ||
    !!f.alertOnly
  );
};

/* Tiny debounce so visibility doesn't toggle for a single render */
function useStabilizedBool(value, delayMs = 120) {
  const [stable, setStable] = useState(value);
  const timerRef = useRef(null);
  useEffect(() => {
    if (stable === value) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStable(value), delayMs);
    return () => clearTimeout(timerRef.current);
  }, [value, delayMs, stable]);
  return stable;
}

/* Build a compact signature of a hits page to detect meaningful changes */
function makeHitsSignature(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return "0|";
  const n = rows.length;
  const first = rows.slice(0, 8).map(r => `${r.hitId}:${r.hitTime}`).join(",");
  const last = rows.slice(-4).map(r => `${r.hitId}:${r.hitTime}`).join(",");
  let maxEpoch = 0;
  for (const r of rows) {
    const t = Date.parse(r.hitTime || 0) || 0;
    if (t > maxEpoch) maxEpoch = t;
  }
  return `${n}|${maxEpoch}|${first}|${last}`;
}

function hasNavPayload(ns) {
  if (!ns || typeof ns !== "object") return false;
  return (
    "vrm" in ns ||
    "filters" in ns ||
    "startDateTime" in ns ||
    "endDateTime" in ns ||
    "page" in ns
  );
}

function applyNavState(ns, setTableState) {
  const next = {};
  if ("page" in ns) next.currentPage = ns.page ?? 1;
  if ("vrm" in ns) next.currentVRMFilter = ns.vrm || "";
  if ("startDateTime" in ns || "endDateTime" in ns) {
    next.currentStartDateTime = ns.startDateTime || null;
    next.currentEndDateTime = ns.endDateTime || null;
  }
  if ("filters" in ns) {
    next.currentFilters = { ...DEFAULT_FILTERS, ...(ns.filters || {}) };
  }
  next.isLoading = true;
  setTableState((prev) => ({ ...prev, ...next }));
}

function MainTable({ tableState, setTableState }) {
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("rowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Hydration / navigation seeds
  const [hydrated, setHydrated] = useState(false);
  const initialNav = useRef(location.state || null);

  // Sequence guard to ignore stale responses
  const requestSeqRef = useRef(0);
  const lastSigRef = useRef(null);

  const nfGB = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

  function withDateHeaders(rows, tz = "Europe/London") {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const out = [];
    let lastKey = null;

    const now = moment.tz(tz).startOf("day");
    for (const r of rows) {
      const m = moment.utc(r.hitTime).tz(tz);
      const day = m.clone().startOf("day");
      const key = day.format("YYYY-MM-DD");

      if (key !== lastKey) {
        const diff = day.diff(now, "days");
        const label =
          diff === 0 ? "Today" : diff === -1 ? "Yesterday" : m.format("dddd, Do MMMM YYYY");
        out.push({ __isDateHeader: true, __dateKey: key, __label: label });
        lastKey = key;
      }
      out.push(r);
    }
    return out;
  }

  // Stable setters
  const SetPageChanged = useCallback(
    (currentPage) => setTableState((prev) => ({ ...prev, currentPage })),
    [setTableState]
  );
  const SetVrmChanged = useCallback(
    (currentVRMFilter) => setTableState((prev) => ({ ...prev, currentVRMFilter })),
    [setTableState]
  );
  const SetFiltersChanged = useCallback(
    (currentFilters) => setTableState((prev) => ({ ...prev, currentFilters })),
    [setTableState]
  );
  const SetStartEndDateChanged = useCallback(
    ({ startDateTime, endDateTime }) =>
      setTableState((prev) => ({
        ...prev,
        currentStartDateTime: startDateTime || null,
        currentEndDateTime: endDateTime || null,
      })),
    [setTableState]
  );

  // Initial hydrate (deep-link state)
  useEffect(() => {
    if (!hydrated) {
      const ns = initialNav.current;
      if (hasNavPayload(ns)) {
        applyNavState(ns, setTableState);
        // clear the state so back/forward stays clean
        navigate(location.pathname, { replace: true, state: {} });
      }
      setHydrated(true);
    }
  }, [hydrated, navigate, location.pathname, setTableState]);

  // Apply navigation state even after mount (e.g. dashboard clicks while on "/")
  useEffect(() => {
    if (!hydrated) return;
    const ns = location.state;
    if (!hasNavPayload(ns)) return;
    applyNavState(ns, setTableState);
    // clear it immediately to avoid re-applying on back/forward
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, hydrated, navigate, location.pathname, setTableState]);


  // Show spinner if we mount with no data
  useEffect(() => {
    if (!tableState.rowData || tableState.rowData.length === 0) {
      setTableState((prev) => ({ ...prev, isLoading: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any parameter change -> trigger a fetch (and show spinner)
  useDidMountEffect(() => {
    setTableState((prev) => ({ ...prev, isLoading: true }));
  }, [
    tableState.currentPage,
    tableState.currentVRMFilter,
    tableState.currentStartDateTime,
    tableState.currentEndDateTime,
    tableState.currentFilters,
    rowsPerPage,
  ]);

  // Dashboard update signal
  const maybeDispatchHitsUpdated = useCallback((rawData, source = "fetch") => {
    const sig = makeHitsSignature(rawData);
    if (sig && sig !== lastSigRef.current) {
      lastSigRef.current = sig;
      try {
        window.dispatchEvent(
          new CustomEvent("hits-updated", {
            detail: { sig, source, at: Date.now(), count: Array.isArray(rawData) ? rawData.length : 0 },
          })
        );
      } catch { }
    }
  }, []);

  // Primary fetch (race-proofed)
  useEffect(() => {
    if (!hydrated) return;
    const seq = ++requestSeqRef.current;

    getData(
      tableState.currentPage,
      rowsPerPage,
      tableState.currentVRMFilter,
      tableState.currentStartDateTime,
      tableState.currentEndDateTime,
      tableState.currentFilters
    )
      .then(({ pages, count, data }) => {
        if (requestSeqRef.current !== seq) return;
        maybeDispatchHitsUpdated(data, "fetch");
        const decorated = withDateHeaders(data);
        setTableState((prev) => ({ ...prev, isLoading: false, rowData: decorated, pages, count }));
      })
      .catch(() => {
        if (requestSeqRef.current !== seq) return;
        setTableState((prev) => ({ ...prev, isLoading: false, rowData: [], pages: 1, count: 0 }));
      });
  }, [
    hydrated,
    tableState.currentPage,
    tableState.currentVRMFilter,
    tableState.currentStartDateTime,
    tableState.currentEndDateTime,
    tableState.currentFilters,
    rowsPerPage,
    setTableState,
    maybeDispatchHitsUpdated,
  ]);

  // Light polling (60s) as a safety net
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        tableState.currentPage === 1 &&
        !tableState.isLoading &&
        !tableState.currentStartDateTime &&
        !tableState.currentEndDateTime
      ) {
        const seq = ++requestSeqRef.current;
        getData(
          tableState.currentPage,
          rowsPerPage,
          tableState.currentVRMFilter,
          tableState.currentStartDateTime,
          tableState.currentEndDateTime,
          tableState.currentFilters
        )
          .then(({ pages, count, data }) => {
            if (requestSeqRef.current !== seq) return;
            maybeDispatchHitsUpdated(data, "poll");
            const decorated = withDateHeaders(data);
            setTableState((prev) => ({ ...prev, rowData: decorated, pages, count }));
          })
          .catch(() => { });
      }
    }, 300_000);
    return () => clearInterval(interval);
  }, [
    tableState.isLoading,
    tableState.currentPage,
    tableState.currentVRMFilter,
    tableState.currentStartDateTime,
    tableState.currentEndDateTime,
    tableState.currentFilters,
    rowsPerPage,
    setTableState,
    maybeDispatchHitsUpdated,
  ]);

  // React to pushes (alert type/list changes and explicit refresh requests)
  useEffect(() => {
    const refetch = () => {
      const seq = ++requestSeqRef.current;

      // If we have no rows yet, allow spinner; otherwise keep it light
      setTableState(prev =>
        (Array.isArray(prev.rowData) && prev.rowData.length > 0)
          ? prev
          : { ...prev, isLoading: true }
      );

      getData(
        tableState.currentPage,
        rowsPerPage,
        tableState.currentVRMFilter,
        tableState.currentStartDateTime,
        tableState.currentEndDateTime,
        tableState.currentFilters
      )
        .then(({ pages, count, data }) => {
          if (requestSeqRef.current !== seq) return;
          maybeDispatchHitsUpdated(data, "push");
          const decorated = withDateHeaders(data);
          setTableState((prev) => ({
            ...prev,
            isLoading: false,
            rowData: decorated,
            pages,
            count,
          }));
        })
        .catch(() => {
          if (requestSeqRef.current !== seq) return;
          setTableState((prev) => ({ ...prev, isLoading: false }));
        });
    };

    window.addEventListener("alertlist-updated", refetch);
    window.addEventListener("alerttypes-updated", refetch);
    window.addEventListener("request-hits-refresh", refetch);
    return () => {
      window.removeEventListener("alertlist-updated", refetch);
      window.removeEventListener("alerttypes-updated", refetch);
      window.removeEventListener("request-hits-refresh", refetch);
    };
  }, [
    tableState.currentPage,
    tableState.currentVRMFilter,
    tableState.currentStartDateTime,
    tableState.currentEndDateTime,
    tableState.currentFilters,
    rowsPerPage,
    setTableState,
    maybeDispatchHitsUpdated,
  ]);

  /* Dashboard visibility */
  const dashboardVisibleRaw = useMemo(() => {
    const hasVrm = !!(tableState.currentVRMFilter && String(tableState.currentVRMFilter).trim() !== "");
    const hasRange = !!(tableState.currentStartDateTime || tableState.currentEndDateTime);
    const panelOn = isPanelFiltersActive(tableState.currentFilters);
    return !(hasVrm || hasRange || panelOn);
  }, [
    tableState.currentVRMFilter,
    tableState.currentStartDateTime,
    tableState.currentEndDateTime,
    tableState.currentFilters,
  ]);
  const dashboardVisible = useStabilizedBool(dashboardVisibleRaw, 140);

  return (
    <div className="App">
      <Pagination
        inCurrentPage={tableState.currentPage}
        inCurrentVRMFilter={tableState.currentVRMFilter}
        inCurrentFilters={tableState.currentFilters}
        inStartDateTime={tableState.currentStartDateTime}
        inEndDateTime={tableState.currentEndDateTime}
        totalRows={tableState.count}
        rowsPerPage={rowsPerPage}
        pageChangeHandler={SetPageChanged}
        vrmFilterChangeHandler={SetVrmChanged}
        dateRangeChangeHandler={SetStartEndDateChanged}
        filtersChangeHandler={SetFiltersChanged}
      />

      <div className={`dash-collapse ${dashboardVisible ? "is-open" : "is-closed"}`} aria-hidden={!dashboardVisible}>
        <div className="container">
          <Dashboard />
        </div>
      </div>

      <div className="table-container">
        <Table
          columns={columns}
          data={tableState.rowData}
          isLoading={tableState.isLoading}
          manualPagination={true}
          onRowClick={(row) => navigate("/detail/" + row.hitId, { state: row })}
          onClearFilters={() => {
            setTableState((prev) => ({
              ...prev,
              currentPage: 1,
              currentVRMFilter: "",
              currentStartDateTime: null,
              currentEndDateTime: null,
              currentFilters: { ...DEFAULT_FILTERS },
              isLoading: true,
            }));
          }}
        />
      </div>

      <div className="table-footer">
        <div className="footer-center">
          {!tableState.isLoading && (
            <div className="page-info">
              Page {nfGB.format(Number(tableState.currentPage || 0))} of {nfGB.format(Number(tableState.pages || 1))}
            </div>
          )}
        </div>

        <div className="footer-right">
          {!tableState.isLoading && (
            <div className="rows-select">
              <label htmlFor="rows-per-page">Rows per page</label>
              <select
                id="rows-per-page"
                value={rowsPerPage}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setRowsPerPage(v);
                  localStorage.setItem("rowsPerPage", v);
                  setTableState((prev) => ({ ...prev, currentPage: 1, isLoading: true }));
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MainTable;
